// WS8 WS-5 — TPS (Third-Party Servicer) registry list at
// /portfolio-admin/tps. Each loan is assigned exactly one TPS; the platform
// fee and NSF lines route to its primary operating account. Mirrors the
// ServiceProviderListView shape but lives under Portfolio Admin / Finance.

import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Badge, Button } from "@/design-system/components/ui";
import {
  OverflowMenu,
  type OverflowMenuItem,
} from "@/design-system/components/ui/OverflowMenu";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import { useEntityNameMap } from "@/hooks/capitalMarkets/useCounterpartyNames";
import { tpsDisplayName } from "@/lib/tpsDisplay";
import {
  useDeleteTps,
  useSetDefaultTps,
  useSetTpsStatus,
  useTpsList,
} from "@/hooks/portfolioAdmin/useTps";
import type { TpsDto, TpsStatus } from "@/types/tps";
import { formatDate, humanizeStatus } from "../../capital-markets/shared/format";
import { NewTpsModal } from "./NewTpsModal";

const STATUSES: TpsStatus[] = ["Active", "Inactive"];

function TpsStatusBadge({ status }: { status: TpsStatus }) {
  return (
    <Badge status={status === "Active" ? "finalized" : "archived"}>
      {humanizeStatus(status)}
    </Badge>
  );
}

export default function TpsListPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("Tps.Manage");

  const [statusFilter, setStatusFilter] = useState<"" | TpsStatus>("");
  const [showNew, setShowNew] = useState(false);

  const listQuery = useTpsList({ pageSize: 200 });
  const { map: entityNames } = useEntityNameMap();
  const setDefault = useSetDefaultTps();
  const setStatus = useSetTpsStatus();
  const del = useDeleteTps();
  const [pendingDelete, setPendingDelete] = useState<TpsDto | null>(null);

  const rows = useMemo<TpsDto[]>(() => {
    const all = listQuery.data?.data ?? [];
    return statusFilter ? all.filter((p) => p.status === statusFilter) : all;
  }, [listQuery.data, statusFilter]);

  const nameOf = (p: TpsDto) =>
    tpsDisplayName(p, entityNames.get(p.entityId));

  const handleSetDefault = (p: TpsDto) => {
    setDefault
      .mutateAsync(p.id)
      .then(() => toast.success(`${nameOf(p)} is now the default TPS.`))
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  const handleToggleActive = (p: TpsDto) => {
    const next = p.status === "Inactive" ? "Active" : "Inactive";
    setStatus
      .mutateAsync({ id: p.id, status: next })
      .then(() =>
        toast.success(
          next === "Active"
            ? `${nameOf(p)} reactivated.`
            : `${nameOf(p)} deactivated.`,
        ),
      )
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    const p = pendingDelete;
    del
      .mutateAsync(p.id)
      .then(() => {
        toast.success(`${nameOf(p)} deleted.`);
        setPendingDelete(null);
      })
      .catch((err) => {
        toast.error(extractErrorMessage(err));
        setPendingDelete(null);
      });
  };

  const columns: Column<TpsDto>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        render: (p) => (
          <div>
            <Link
              to={`/portfolio-admin/tps/${p.id}`}
              className="font-semibold hover:text-gold-shadow"
              data-testid={`tps-row-${p.id}`}
            >
              {nameOf(p)}
            </Link>
            {p.isPlaceholder && (
              <p className="m-0 text-caption text-gray-mid">
                Placeholder — holds unassigned loans
              </p>
            )}
          </div>
        ),
      },
      {
        key: "status",
        header: "Status",
        render: (p) => <TpsStatusBadge status={p.status} />,
      },
      {
        key: "default",
        header: "Default",
        render: (p) =>
          p.isDefault ? <Badge status="gold">Default</Badge> : null,
      },
      {
        key: "assigned",
        header: "Assigned Loans",
        // Assigned-loans count is not yet exposed by the list endpoint; the
        // detail page will surface the join when the backend ships it.
        render: () => 0,
      },
      {
        key: "created",
        header: "Created",
        render: (p) => formatDate(p.createdAt),
      },
      {
        key: "actions",
        header: "",
        className: "w-10 text-right",
        render: (p) => {
          if (!canManage || p.isDefault) return null;
          const items: OverflowMenuItem[] = [
            // "Set as default" is ONLY the placeholder's recovery affordance
            // (re-heal a demoted placeholder). A real TPS can never be the
            // default, so it never gets this item — just Deactivate + Delete.
            ...(p.isPlaceholder
              ? [
                  {
                    label: "Set as default",
                    onSelect: () => handleSetDefault(p),
                  },
                ]
              : []),
            {
              label: p.status === "Inactive" ? "Reactivate" : "Deactivate",
              onSelect: () => handleToggleActive(p),
            },
            {
              label: "Delete permanently…",
              onSelect: () => setPendingDelete(p),
            },
          ];
          return (
            <OverflowMenu
              data-testid={`tps-actions-${p.id}`}
              items={items}
            />
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entityNames, canManage],
  );

  return (
    <div className="flex flex-col gap-space-3" data-testid="tps-list-page">
      <PageHeader
        eyebrow="Portfolio Administration"
        title="TPS Providers"
        description="Third-Party Servicer firms — each loan is assigned one TPS; the platform fee and NSF lines route to its primary operating account."
      />

      <div className="flex flex-wrap items-center justify-between gap-space-3">
        <Select
          aria-label="Filter by status"
          className="w-44"
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter((e.target.value as TpsStatus) || "")
          }
          data-testid="tps-status-filter"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {humanizeStatus(s)}
            </option>
          ))}
        </Select>
        {canManage && (
          <Button
            onClick={() => setShowNew(true)}
            data-testid="tps-add-button"
          >
            <Plus size={16} strokeWidth={2} aria-hidden /> New TPS
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        loading={listQuery.isLoading}
        error={
          listQuery.isError ? "Could not load TPS providers." : null
        }
        emptyMessage="No TPS providers yet. Create your first record to begin."
        rowKey={(p) => p.id}
      />

      {showNew && (
        <NewTpsModal
          onClose={() => setShowNew(false)}
          onCreated={(id) => {
            setShowNew(false);
            navigate(`/portfolio-admin/tps/${id}`);
          }}
        />
      )}

      {pendingDelete && (
        <Modal
          open
          onClose={() => setPendingDelete(null)}
          title="Delete TPS provider?"
          description={`Permanently remove ${nameOf(pendingDelete)}. This only works if no loans are assigned — otherwise deactivate it instead.`}
          size="sm"
          footer={
            <div className="flex items-center justify-end gap-space-2">
              <Button
                variant="text"
                size="sm"
                onClick={() => setPendingDelete(null)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                loading={del.isPending}
                onClick={handleConfirmDelete}
                data-testid="tps-delete-confirm"
              >
                Delete
              </Button>
            </div>
          }
        >
          <p className="text-small text-gray-mid m-0">
            If this servicer currently services loans, the delete is blocked —
            deactivate it instead so existing loans keep resolving correctly.
          </p>
        </Modal>
      )}
    </div>
  );
}
